import { NgClass } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericCardComponent } from '../../../../shared/components/generic-card/generic-card.component';
import { Messages } from '../../../../shared/constants/messages';
import { ApiResponse } from '../../../../shared/model/api-response';
import { GameTable } from '../../../../shared/model/game-table';
import { GameTableParticipation } from '../../../../shared/model/game-table-participation';
import { ItalianDatePipe } from '../../../../shared/pipes/italian-date.pipe';
import { AlertService } from '../../../../shared/services/alert.service';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { GameTableParticipationService } from '../../services/game-table-participation.service';
import { GameTablesService } from '../../services/gametables.service';
import { GeoLocationService } from '../../services/geo-location.service';
import { GameTableEventCardComponent } from './game-table-event-card/game-table-event-card.component';
import { GameTableSessionsComponent } from './game-table-sessions/game-table-sessions.component';
import { PermissionService } from '../../../../core/services/permission.service';
import { SystemPower } from '../../../../shared/model/permissions';
import { GameTableParticipationRequest } from '../../../../shared/model/game-table-participation-request';
import { NavigationHistoryService } from '../../../../core/services/navigation-history.service';
import { OsmMapComponent } from '../../../../shared/components/osm-map/osm-map.component';
import { tableMapMarkers } from '../../../../shared/utils/map-markers';
import { parseCoordinates } from '../../../../shared/utils/geo-coordinates';
import { MapMarker } from '../../../../shared/model/map-marker';

@Component({
  selector: 'app-game-table-detail',
  standalone: true,
  imports: [
    ItalianDatePipe,
    GenericCardComponent,
    NgClass,
    GameTableEventCardComponent,
    GameTableSessionsComponent,
    OsmMapComponent,
  ],
  templateUrl: './game-table-detail.component.html',
  styleUrl: './game-table-detail.component.css',
})
export class GameTableDetailComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private gameTableService: GameTablesService = inject(GameTablesService);
  private gameTableParticipationService: GameTableParticipationService = inject(
    GameTableParticipationService,
  );
  private alertService: AlertService = inject(AlertService);
  private confirmDialogService: ConfirmDialogService = inject(ConfirmDialogService);
  private permissionService: PermissionService = inject(PermissionService);
  private navigationHistory: NavigationHistoryService = inject(NavigationHistoryService);
  protected geoLocationService: GeoLocationService = inject(GeoLocationService);
  private tableId: string | null = null;
  protected table = signal<GameTable | undefined>(undefined);
  protected participationsSorted = signal<Array<GameTableParticipation>>([]);
  protected locationMarkers = signal<MapMarker[]>([]);
  protected usesEventLocation = signal<boolean>(false);

  ngOnInit(): void {
    this.tableId = this.route.snapshot.paramMap.get('id');
    if (this.tableId) {
      this.loadTable();
    }
  }

  loadTable() {
    if (this.tableId) {
      const tableId = Number(this.tableId);
      this.gameTableService.getTableById(tableId).subscribe({
        next: (response: ApiResponse<GameTable>) => {
          if (response.success && response.data) {
            this.table.set(response.data);
            this.locationMarkers.set(tableMapMarkers([response.data]));
            this.usesEventLocation.set(
              !parseCoordinates(response.data.location) &&
              !!parseCoordinates(response.data.event?.location)
            );
            this.participationsSorted.set(
              response.data.participations.sort(
                (a, b) => a.userProfile.userId - b.userProfile.userId,
              )
            );
          }
        },
        error: () => {
          this.alertService.showError(Messages.TABLE_LOAD_ERROR);
          this.router.navigate(['/tables']);
        },
      });
    } else {
      this.alertService.showError(Messages.TABLE_NOT_FOUND);
    }
  }

  accept(participation: GameTableParticipation) {
    this.gameTableParticipationService.acceptParticipation(participation.id).subscribe({
      next: (response: ApiResponse<GameTableParticipation>) => {
        if (response.success && response.data) {
          this.loadTable();
        }
      },
      error: () => {
        this.alertService.showError(Messages.TABLE_PARTICIPATION_ERROR);
      },
    });
  }

  async remove(participation: GameTableParticipation | undefined) {
    if (!participation) {
      this.alertService.showError(Messages.TABLE_PARTICIPATION_REMOVE_ERROR);
      return;
    }
    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_PARTICIPATION_REMOVE_TITLE,
      message: Messages.CONFIRM_PARTICIPATION_REMOVE,
      confirmLabel: Messages.CONFIRM_REMOVE,
    });
    if (!accepted) {
      return;
    }
    this.gameTableParticipationService.removeParticipation(participation.id).subscribe({
      next: (response: ApiResponse<string>) => {
        if (response.success) {
          this.loadTable();
        }
      },
      error: () => {
        this.alertService.showError(Messages.TABLE_PARTICIPATION_REMOVE_ERROR);
      },
    });
  }

  getUserParticipation() {
    const user = this.permissionService.getCurrentUser();
    if (user) {
      return this.table()?.participations.find(
        (participation: GameTableParticipation) =>
          participation.userProfile.userName === user.userName ||
          participation.userProfile.userId == user.id,
      );
    }
    return;
  }

  isParticipant() {
    return this.table()?.isParticipant;
  }

  isCreator() {
    return this.table()?.isCreator;
  }

  canManage(): boolean {
    return this.permissionService.canManageTable(this.table());
  }

  canJoin(): boolean {
    const table = this.table();
    return !!table && !table.isParticipant && table.isActive;
  }

  isUserCreator(userId: number) {
    return this.table()?.creator && this.table()!.creator.userId == userId;
  }

  goBack() {
    this.navigationHistory.goBack(['/tables']);
  }

  edit() {
    if (!this.canManage()) {
      return;
    }
    this.router.navigate(['/tables/edit', this.table()?.id]);
  }

  async deleteTable() {
    const table = this.table();
    if (!this.canManage() || !table?.id) {
      return;
    }
    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_TABLE_DELETE_TITLE,
      message: Messages.confirmTableDelete(table.tableName),
      confirmLabel: Messages.CONFIRM_DELETE,
    });
    if (!accepted) {
      return;
    }
    this.gameTableService.deleteTable(table.id).subscribe({
      next: (response: ApiResponse<string>) => {
        if (response.success) {
          this.alertService.showSuccess(Messages.TABLE_DELETED_SUCCESS);
          this.router.navigate(['/tables']);
        }
      },
      error: () => {
        this.alertService.showError(Messages.TABLE_DELETE_ERROR);
      },
    });
  }

  joinTable() {
    const table = this.table();
    if (!this.canJoin() || !table?.id) {
      return;
    }
    const user = this.permissionService.getCurrentUser();
    if (!user) {
      this.alertService.showError(Messages.LOGIN_REQUIRED);
      return;
    }
    const gameTableParticipateRequest: GameTableParticipationRequest = {
      tableId: table.id,
      userId: user.id,
    };
    this.gameTableParticipationService.participateToTable(gameTableParticipateRequest).subscribe({
      next: (response: ApiResponse<GameTableParticipation>) => {
        if (response.success && response.data) {
          this.alertService.showSuccess(Messages.TABLE_PARTICIPATION_SUCCESS);
          this.loadTable();
        } else {
          this.alertService.showError(Messages.TABLE_PARTICIPATION_ERROR);
        }
      },
      error: () => {
        this.alertService.showError(Messages.TABLE_PARTICIPATION_ERROR);
      },
    });
  }

  onSessionsChanged() {
    this.loadTable();
  }
}
